import 'mapbox-gl/dist/mapbox-gl.css'

import * as turf from '@turf/turf'
import { useRuntimeVariables } from 'app-kit'
import MapPin from 'assets/map-pin.svg?react'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { Map as MapboxMap, MapRef, Marker, MarkerDragEvent } from 'react-map-gl'
import styled from 'styled-components'
import { mapStyle } from 'styles/mapBox'

const MapPinIcon = styled(MapPin)`
  width: 18px;
  height: 18px;
  stroke: ${({ theme }) => theme.colors.highVis};
`

const buffer = {
  number: 100,
  units: 'feet' as turf.Units,
}

export const LocationMap: React.FC<{
  geography: turf.Geometry | undefined
  draggable?: boolean
  onMarkerDragEnd?: (longitude: number, latitude: number) => void
}> = ({ geography, draggable = false, onMarkerDragEnd }) => {
  const { MAPBOX_KEY } = useRuntimeVariables()
  const ref = useRef<MapRef>(null)

  const [hasLoaded, setHasLoaded] = useState<boolean>(false)

  const mapConfig = useMemo(() => {
    // if there's no location, center the map to the middle of the US
    if (!geography) {
      return {
        bounds: null,
        longitude: -99.15167556674915,
        latitude: 38.96283407369589,
      }
    }

    const feature: turf.Feature = {
      type: 'Feature',
      geometry: geography,
      properties: {},
    }
    const bounds = turf.bbox(
      turf.buffer(feature, buffer.number, { units: buffer.units })
    ) as [number, number, number, number]

    const point = turf.centroid(geography)
    const [longitude, latitude] = point.geometry.coordinates
    return { bounds, longitude, latitude }
  }, [geography])

  const handleMarkerDragEnd = useCallback(
    (event: MarkerDragEvent) => {
      if (onMarkerDragEnd && event.lngLat) {
        const { lngLat } = event
        onMarkerDragEnd(lngLat.lng, lngLat.lat)
      }
    },
    [onMarkerDragEnd]
  )

  return (
    <MapboxMap
      ref={ref}
      interactive={draggable}
      onRender={(e) => {
        setHasLoaded((prev) => {
          if (!prev && mapConfig.bounds)
            e.target.fitBounds(mapConfig.bounds, {
              animate: false,
              padding: { top: 50, bottom: 50, left: 50, right: 50 },
            })
          return true
        })
      }}
      mapboxAccessToken={MAPBOX_KEY}
      mapStyle={mapStyle}
      initialViewState={{
        latitude: mapConfig.latitude,
        longitude: mapConfig.longitude,
        zoom: 1, // An appropriate zoom level for the global view
      }}
      style={{
        width: '100%',
        height: '100%',
        borderRadius: '6px',
        transition: 'opacity 300ms',
        opacity: hasLoaded ? 1 : 0,
      }}>
      {geography && (
        <Marker
          longitude={mapConfig.longitude}
          latitude={mapConfig.latitude}
          anchor='bottom'
          draggable={draggable}
          onDrag={handleMarkerDragEnd}>
          <MapPinIcon />
        </Marker>
      )}
    </MapboxMap>
  )
}
